import { Location, formatDate } from '@angular/common';
import { Component, effect, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { GeolocationService } from '@ng-web-apis/geolocation';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AddCatchHmsDialogComponent } from '../add-catch-hms-dialog/add-catch-hms-dialog.component';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { CoordinateDialogComponent } from '../set-detail/coordinate-dialog/coordinate-dialog.component';
import { Catch } from '../shared/catch.model';
import { Coordinate } from '../shared/coordinate.model';
import { FishingEvent } from '../shared/fishing-event.model';
import { Gear } from '../shared/gear.model';
import { Fishery, LocalStorageService, StorageResult } from '../shared/local-storage.service';
import { coordValidator, maxDateValidator } from '../shared/trip-validator.model';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { LookupItem } from '../shared/lookup-item.model';
import { LookupDataService } from '../shared/lookup-data.service';
import { Insights } from '../shared/insights.service';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && (form?.errors?.invalid || control.hasError('required')) && (control.dirty || control.touched || isSubmitted));
  }
}


interface QuerySchema {
  setIndex: number,
  isNewSet: boolean,
  completedSetIndex: number,
  editedSetIndex: number,
  previousScreen: string, 
  readOnly: boolean,
  draftIndex: number,
  draftSetId: string
};


let SCREEN_WIDTH: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';

/**
 * HMS Set details, all set information recorded here. 
 * All updates trigger database update.
 * From here we use various dialogs, for gears, catch, coordinates.
 */

@Component({
    selector: 'app-set-detail-hms',
    templateUrl: './set-detail-hms.component.html',
    styleUrl: './set-detail-hms.component.css',
    standalone: false
})

export class SetDetailHmsComponent {

  /**
   * The is the fishing event (set) the component is detailing
   */
  fishingEvent: FishingEvent = new FishingEvent('', '');
  prevSet: FishingEvent = new FishingEvent('', '');
  setIndex!: number;
  isNewSet: boolean = false;
  isReadOnly: boolean = false;

  /**
   * If -1, this set is not part of the landing, otherwise it's the index into the landing sets array
   */
  partOfLandingInd: number = -1;

  /**
   * If not -1 then the set is part of an edited logbook
   */
  partOfEditedLanding: number = -1;

  partOfDraftTrip: number = -1;

  draftSetId: string = '';

  firstGear: boolean = false;

  errorNumber: number = 0;

  catchTableDefaultColumns: string[] = ['Species', 'RetainedCount', 'ReleasedCount'];
  catchTableHarpoonColumns: string[] = ['Species', 'RetainedCount', 'RetainedWeight', 'ReleasedCount', 'ReleasedWeight'];
  @ViewChild(MatTable) table!: MatTable<Catch>;

  /**
   * Subscribe to form update. All changes trigger database changes and updates.
   */
  formUpdateSubscription: Subscription = new Subscription();
  /* Form Fields */
  stringNum: FormControl = new FormControl('', [Validators.required, Validators.pattern('^[a-zA-Z0-9<>?\/!@#$%^&*()\. ]+$'), Validators.maxLength(10)]);
  setDetailForm: FormGroup = new FormGroup({
    fishingDate: new FormControl('', [Validators.required, maxDateValidator()]),
    gear: new FormControl<string>('', [Validators.required]),
    dsbgType: new FormControl<LookupItem>(new LookupItem('')),
    fishingMode: new FormControl('', [Validators.required]),
    lat: new FormControl(new Coordinate()),
    lon: new FormControl(new Coordinate()),
    hoursFished: new FormControl('', [Validators.required, Validators.min(0), Validators.max(24)]),
    numBuoys: new FormControl('', [Validators.min(0), Validators.max(10)]),
    numHooks: new FormControl('', [Validators.min(0), Validators.max(30)]), 
    aircraftUsed: new FormControl<Boolean>(false),
    comments: new FormControl('')
  });

  gearSubscription!: Subscription;
  hoursSubscription!: Subscription;

  gearOptions: string[] = LookupDataService._HmsGearTypes.map(g => g.name);
  gearSubtypes: LookupItem[] = LookupDataService._HmsDBSGTypes;

  /**
   * For geolocation updates
   */
  locationSubscription: Subscription = new Subscription();

  parameterSubscription!: Subscription;
  /**
   * The coordinates returned from geolocation API
   */
  position!: GeolocationCoordinates;
  locationAllowed: boolean = false;

  /**
   * For use on lost skates/tubs/pots form field. Needed for validation purposes.
   */
  matcher = new MyErrorStateMatcher();

  disableAdd: boolean = false;

  userSetsSubscription!: Subscription; //Not for o.e.s

  //Edited landing subscription instead

  previousScreen: string = 'menu';


  constructor(
    public dataSync: LocalStorageService, 
    public _location: Location, 
    public router: Router, 
    public route: ActivatedRoute, 
    public dialog: MatDialog, 
    private readonly geolocation$: GeolocationService, 
    private toastr: ToastrService, 
    private insights: Insights) { 
      let editedIndex = +this.route.snapshot.queryParams.editedIndex;
      if (isNaN(editedIndex) && editedIndex > -1) {
        effect(() => {this.dataSync.edited_hms(); this.initialize();});
      } else {
        effect(() => {this.dataSync.user_trip(); this.initialize();});
      }
      
      this.parameterSubscription = this.route.params.subscribe(_ => this.initialize());
      this.locationSubscription = this.geolocation$.subscribe(pos => this.position = pos.coords, err => {
        
        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
        }
      });
    }


    collectQueryParameters() {
      //Required route parameters

      //The set index will be > -1 if this is a current trip set or a read-only view of a set of a completed delivery
      let setIndex = Number(this.route.snapshot.paramMap.get('index') ?? -1);
      let isNewSet = this.route.snapshot.paramMap.get('isNew') === 'true';

      //Optional query parameters

      let readOnly = this.route.snapshot.queryParams['readOnly'] === 'true';
      let previousScreen = this.route.snapshot.queryParams['previous'] ?? 'menu';
      //completedInd will be the index into the received logbooks array - in this case setIndex will index into the set of the completed logbook
      let completedInd = Number(this.route.snapshot.queryParams['completedIndex'] ?? -1);
      //editedIndex will be the index of the set of the currently edited delivery
      let editedIndex = Number(this.route.snapshot.queryParams['editedIndex'] ?? -1);

      let partOfDraftTrip = Number(this.route.snapshot.queryParams['draftIndex'] ?? -1);
      let draftSetId = this.route.snapshot.queryParams.draftSetId;

      let schema: QuerySchema = {
        setIndex: setIndex,
        isNewSet: isNewSet,
        previousScreen: previousScreen,
        completedSetIndex: completedInd,
        editedSetIndex: editedIndex,
        readOnly: readOnly,
        draftIndex: partOfDraftTrip,
        draftSetId: draftSetId
      };

      return schema;
    }

    calculateErrors() {
      this.errorNumber = 0;
      for (const control in this.setDetailForm.controls)
        for (const err in this.setDetailForm.controls[control].errors)
          this.errorNumber++;
      if (this.setDetailForm.errors) this.errorNumber++;
    }


  /**
   * Fill the form if editing a set and set dynamic updates on the set form.
   */
  initialize() {
    let queryArgs = this.collectQueryParameters();

     if (queryArgs.readOnly) {
      this.setDetailForm.disable();
      this.stringNum.disable();
      this.isReadOnly = true;
    }

    const activeTrip = this.dataSync.user_trip();

    if (!activeTrip) {
      this.insights.trackTrace('HMSSetDetailComponent: No active trip found');
      this.router.navigate(['menu']);
      return;
    } 

    this.formUpdateSubscription?.unsubscribe();

    this.isNewSet = queryArgs.isNewSet;
    this.previousScreen = queryArgs.previousScreen;
    this.setIndex = queryArgs.setIndex;
    let editedIndex = queryArgs.editedSetIndex;
    let completedInd = queryArgs.completedSetIndex;
    this.partOfDraftTrip = queryArgs.draftIndex;
    this.draftSetId = queryArgs.draftSetId;

    if (!activeTrip) this.insights.trackTrace('HMSSetDetailComponent: No active trip found');
    else if (!activeTrip!.sets[this.setIndex]) this.insights.trackTrace('SetDetailComponent: Missing set at index ' + this.setIndex.toString());

    if (this.partOfDraftTrip > -1 ) this.fishingEvent = this.dataSync.hms_drafts()[this.partOfDraftTrip].sets.find(fs => fs.fishingEventID == this.draftSetId)!
    else if (editedIndex > -1) this.fishingEvent = this.dataSync.edited_hms().sets[editedIndex];
    else if (this.setIndex > -1 && completedInd > -1) this.fishingEvent = this.dataSync.received_logbooks()[completedInd].sets[this.setIndex];
    else if (this.setIndex > -1) this.fishingEvent = activeTrip!.sets[this.setIndex];
    else {
      this.insights.trackTrace('HMSSetDetailComponent: Unable to initialize set with query/route parameters');
      this.router.navigate(['menu']);
      return;
    }
    
    for (var ind=this.setIndex-1; ind>=0; ind--) {
      if (activeTrip!.sets[ind].fishery == 'HMS') {
        this.prevSet = activeTrip!.sets[ind];
        break;
      } 
    }

    if (!this.fishingEvent) {
      this.insights.trackTrace('HMSSetDetailComponent: Fishing event is undefined');
      this.router.navigate(['menu']);
      return;
    } 

    this.partOfLandingInd = activeTrip ? activeTrip.hmsDelivery.sets.findIndex((s: FishingEvent) => s.fishingEventID === this.fishingEvent.fishingEventID) : -1;
    this.partOfEditedLanding = this.dataSync.edited_hms().sets.findIndex((s: FishingEvent) => s.fishingEventID == this.fishingEvent.fishingEventID);

    let gearEntry: Gear;
    if (this.fishingEvent.gear.type === "") gearEntry = Gear.transformAnonymous(this.prevSet.gear); // If no gear is selected on this set, fill the gear from the previous set
    else gearEntry = Gear.transformAnonymous(this.fishingEvent.gear);

    this.stringNum.setValue(this.fishingEvent.setNumber);
    this.setDetailForm.patchValue({
      fishingDate: this.fishingEvent.startDate ? formatDate(this.fishingEvent.startDate, 'EEEE, MMM d, y', 'en-US') : '',
      gear: gearEntry.type,
      dsbgType: gearEntry.gearSubtype,
      fishingMode: this.fishingEvent.fishingMode,
      lat: this.fishingEvent.startLat,
      lon: this.fishingEvent.startLon,
      hoursFished: this.fishingEvent.hoursFished == 0 ? 0 : this.fishingEvent.hoursFished ? this.fishingEvent.hoursFished : '',
      numBuoys: this.fishingEvent.buoyCount == 0 ? 0 : this.fishingEvent.buoyCount ? this.fishingEvent.buoyCount : '',
      numHooks: this.fishingEvent.hookCount == 0 ? 0 : this.fishingEvent.hookCount ? this.fishingEvent.hookCount : '',
      aircraftUsed: this.fishingEvent.aircraftUsed, 
      comments: this.fishingEvent.comments
    });
    this.fishingEvent.gear = gearEntry;
    if (this.fishingEvent.gear.type === "") this.firstGear = true;
    this.subscribeToFormUpdate();

    // Subscribe to 'gear' changes
    this.gearSubscription = this.setDetailForm.get('gear')!.valueChanges.subscribe(newGearValue => {
      this.handleGearChange(newGearValue);
    });

    // Subscribe to 'hoursFished' changes
    this.hoursSubscription = this.setDetailForm.get('hoursFished')!.valueChanges.subscribe(hoursFishedValue => {
      this.updateLatLonValidators(hoursFishedValue);
    });

    // Initial call to set validators based on the initial 'hoursFished' value
    this.updateLatLonValidators(this.setDetailForm.get('hoursFished')?.value);
    this.updateHoursFishedValidators();
    this.calculateErrors();
  }


  compareDSBGtype = (o1: LookupItem, o2: LookupItem): boolean => {
    return o1 && o2 ? o1.name === o2.name : o1 === o2;
  };
  

  /**
   * Set subscriptions for form updates.
   */
  subscribeToFormUpdate() {
    this.formUpdateSubscription.unsubscribe();
    this.formUpdateSubscription = this.setDetailForm.valueChanges.subscribe(value => {

      this.fishingEvent.updateHMS(value);
      //If this event is part of the landing, update the landing too
      this.updateLandingAndEvent();
    });

  }


  /**
   * Handles gear change by setting or removing validators and clearing form values
  */
  handleGearChange(newGearValue: string) {
    const dsbgTypeControl = this.setDetailForm.get('dsbgType');
    const aircraftUsedControl = this.setDetailForm.get('aircraftUsed');
    const numBuoysControl = this.setDetailForm.get('numBuoys');
    const numHooksControl = this.setDetailForm.get('numHooks');

    if (newGearValue === 'Deep Set Buoy Gear') {
      dsbgTypeControl?.setValidators(Validators.required);
      numBuoysControl?.setValidators([Validators.required, Validators.min(0), Validators.max(10)]);
      numHooksControl?.setValidators([Validators.required, Validators.min(0), Validators.max(30)]);
    } else {
      dsbgTypeControl?.clearValidators();
      numBuoysControl?.clearValidators();
      numHooksControl?.clearValidators();
    }

    if (newGearValue === 'Harpoon') {
      aircraftUsedControl?.setValidators(Validators.required);
    } else {
      aircraftUsedControl?.clearValidators();
    }

    dsbgTypeControl?.updateValueAndValidity({emitEvent: false});
    aircraftUsedControl?.updateValueAndValidity({emitEvent: false});
    numBuoysControl?.updateValueAndValidity({emitEvent: false});
    numHooksControl?.updateValueAndValidity({emitEvent: false});


    this.clearFormValues();
    this.calculateErrors();
  }



  /**
   * Certain fields to be cleared when switching gears
  */
  clearFormValues() {
    this.setDetailForm.patchValue({
      dsbgType: '',
      aircraftUsed: null,
      numBuoys: '',
      numHooks: ''
    });
  }


    /**
   * Update the hours fished validation based on catch
   */
  updateHoursFishedValidators() {
    const hoursControl = this.setDetailForm.get('hoursFished');
    // If there are catch items, hoursFished must be > 0
    hoursControl?.setValidators([
      Validators.required, 
      Validators.min((this.fishingEvent.catchItems && this.fishingEvent.catchItems.length > 0) ? 1 : 0), 
      Validators.max(24)
    ]);
    hoursControl?.updateValueAndValidity({ emitEvent: false });
    this.calculateErrors();
  }


  /**
   * Update the latitude & longitude validation based on hours fished
   */
  updateLatLonValidators(hoursFishedValue: number) {
    const latControl = this.setDetailForm.get('lat');
    const lonControl = this.setDetailForm.get('lon');

    if (hoursFishedValue > 0 || (this.fishingEvent.catchItems && this.fishingEvent.catchItems.length > 0)) {
      latControl?.setValidators([Validators.required, coordValidator()]);
      lonControl?.setValidators([Validators.required, coordValidator()]);
    } else {
      latControl?.clearValidators();
      lonControl?.clearValidators();
    }

    // Update the validity state
    latControl?.updateValueAndValidity({emitEvent: false});
    lonControl?.updateValueAndValidity({emitEvent: false});
    this.calculateErrors();
  }


  /**
   * Database updates to current landing and this set.
   */
  updateLandingAndEvent() {
    if (this.partOfLandingInd != -1) {
      this.dataSync.user_trip.update(trip => {
        trip.hmsDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
        return trip;
      })
    } else if (this.setIndex != -1 && this.setDetailForm.valid && this.stringNum.valid) {
      this.dataSync.user_trip.update(trip => {
        trip.hmsDelivery.sets.push(this.fishingEvent);
        return trip;
      });
      this.partOfLandingInd = this.dataSync.user_trip().hmsDelivery.sets.length - 1;
    }

    if (this.partOfEditedLanding != -1) {
      this.dataSync.edited_logbook.update(el => {
        el.HMS.sets[this.partOfEditedLanding] = this.fishingEvent;
        return {...el};
      });
      this.dataSync.updateLanding(Fishery.HMS, true).then((res: StorageResult) => {
        if (!res.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(res.error);
        }
      });
    }

    if (this.partOfDraftTrip !== -1) {
      this.dataSync.hms_drafts.update(drafts => {
        drafts[this.partOfDraftTrip].sets[drafts[this.partOfDraftTrip].sets.findIndex(fs => fs.fishingEventID == this.draftSetId)] = this.fishingEvent;
        return drafts;
      });

      this.dataSync.updateLanding(Fishery.HMS, false, 'default', this.dataSync.hms_drafts()[this.partOfDraftTrip].localLandingGUID, true).then((res: StorageResult) => {
        if (!res.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(res.error);
        }
      });
    }

    if (this.setIndex != -1) this.dataSync.updateTrip().then((res: StorageResult) => {
      if (!res.success) {
        this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
        this.insights.trackException(res.error);
      }
    });
    this.calculateErrors();
  }


  confirmDelete() { //For o.e.s?
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: { screen: 'deleteset' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteSet();
      }
    });
  }


  /**
   * For manually entering of coordinates through the CoordinateDialogComponent.
   * @param title Editing latitude or longitude?
   */
  openCoordinateDialog(title: string) {
    let w: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
    let value: Coordinate = this.setDetailForm.get(title)?.value;
    const dialogRef = this.dialog.open(CoordinateDialogComponent, {
      width: SCREEN_WIDTH > w ? SCREEN_WIDTH : w,
      data: { title: title, coord: value, fishery: 'HMS' },
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        switch (title) {
          case 'lat':
            this.fishingEvent.startLatManualEntry = true;
            break;
          case 'lon':
            this.fishingEvent.startLonManualEntry = true;
            break;
        }
        value = result;
        value.fillDecimal();
        this.formUpdateSubscription.unsubscribe();
        this.setDetailForm.get(title)?.setValue(value);
        this.fishingEvent.updateHMS(this.setDetailForm.value);
        this.updateLandingAndEvent();
        this.subscribeToFormUpdate();
        this.setDetailForm.get(title)?.updateValueAndValidity();
      }
    });
  }


  /**
   * For auto-filling of coordinates and date
   * We have to keep track of whether coordinate data is auto filled or manually entered.
   */
  fillSetDateLocation(dateOnly = false) {
    if (!this.locationAllowed || this.position) {
      this.locationSubscription = this.geolocation$.subscribe(pos => { this.position = pos.coords; this.locationAllowed = true; }, err => {

        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
        }
      });
    }

    let fillFunction = () => {
      this.formUpdateSubscription.unsubscribe();

      if (dateOnly) {
        this.setDetailForm.get('fishingDate')?.setValue(formatDate(new Date(), 'EEEE, MMM d, y', 'en-US'));      
      } else {
        this.setDetailForm.get('lat')?.setValue(Coordinate.fillDegrees(this.position.latitude, this.position.latitude < 0 ? 'S' : 'N'));
        this.setDetailForm.get('lon')?.setValue(Coordinate.fillDegrees(this.position.longitude, this.position.longitude < 0 ? 'W' : 'E'));
        this.fishingEvent.startLatManualEntry = false;
        this.fishingEvent.startLonManualEntry = false;
      }
      this.setDetailForm.markAsDirty();

      this.fishingEvent.updateHMS(this.setDetailForm.value);

      if (this.partOfLandingInd != -1) this.dataSync.user_trip.update(trip => {trip.hmsDelivery.sets[this.partOfLandingInd] = this.fishingEvent; return trip;});
      if (this.partOfEditedLanding != -1) this.dataSync.edited_logbook.update(el => {
        el.HMS.sets[this.partOfEditedLanding] = this.fishingEvent;
        return {...el};
      });
      
      this.dataSync.updateTrip().then(result => {
        if (!result.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(result.error);
        }
        
        this.subscribeToFormUpdate();
        this.setDetailForm.updateValueAndValidity();

      }, _ => {
        this.subscribeToFormUpdate()
        this.setDetailForm.updateValueAndValidity();
      });
    }

    if ((dateOnly && !this.fishingEvent.startDate) ||
      (!dateOnly && this.fishingEvent.startLat.isUnfilled() && this.fishingEvent.startLon.isUnfilled())) {
      fillFunction();
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      data: { screen: 'autofill' }
    }).afterClosed().subscribe(result => {
      if (result) fillFunction();
    });

  }

  /**
   * Manual date/time editing through the DateTimePickerComponent
   */
  editDateTime() {
    let dateInput = this.setDetailForm.get("fishingDate")?.value == '' ? new Date() : new Date(this.setDetailForm.get("fishingDate")?.value);

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      autoFocus: false,
      data: { date: dateInput, isDelivery: false, isHMS: true, isStart: false, isEnd: false},
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        //Adjusting the validation here gives a pouchDB update conflict since a call to the update subscription gets triggered twice
        this.formUpdateSubscription.unsubscribe();
        this.setDetailForm.get("fishingDate")?.setValue(formatDate(result, 'EEEE, MMM d, y', 'en-US'));
        this.fishingEvent.updateHMS(this.setDetailForm.value);
        this.updateLandingAndEvent();
        this.subscribeToFormUpdate();
      }
    });
  }

  /**
   * Open the catch dialog to add fish species
   */
  openCatchDialog(catchIndex: number): void {

    if (this.isReadOnly) return;
    let isNew = false;
    if (catchIndex >= this.fishingEvent.catchItems.length) {
      this.fishingEvent.catchItems.push(new Catch('', true));
      isNew = true;
    }

    const dialogRef = this.dialog.open(AddCatchHmsDialogComponent, {
      autoFocus: false,
      width: '400px',
      height: '600px',
      position: { top: '5px' },
      disableClose: true,
      data: {item: this.fishingEvent.catchItems[catchIndex], isHarpoon: this.fishingEvent.gear.type == 'Harpoon'} //empty if row clicked
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.delete) {
          this.fishingEvent.catchItems.splice(catchIndex, 1);
        } else {
          if (catchIndex >= this.fishingEvent.catchItems.length) this.fishingEvent.catchItems.push(new Catch('', true));
          let item = this.fishingEvent.catchItems[catchIndex];
          item.species = result.species;

          item.retainedCount = result.retainedCount !== undefined && result.retainedCount !== '' ? Number.parseInt(result.retainedCount) : undefined;
          item.retainedWeight = result.retainedWeight != undefined && result.retainedWeight !== '' ? Number.parseInt(result.retainedWeight) : undefined;
          item.releasedCount = result.releasedCount != undefined && result.releasedCount !== '' ? Number.parseInt(result.releasedCount) : undefined;
          item.releasedWeight = result.releasedWeight != undefined && result.releasedWeight !== '' ? Number.parseInt(result.releasedWeight) : undefined;
        }
        this.table.renderRows();

        if (this.partOfLandingInd != -1) {
          this.dataSync.user_trip.update(trip => {
            trip.hmsDelivery.sets.push(this.fishingEvent);
            return trip;
          });
        }
        if (this.partOfEditedLanding != -1) {
          this.dataSync.edited_logbook.update(el => {
            el.HMS.sets[this.partOfEditedLanding] = this.fishingEvent;
            return {...el};
          });
          this.updateLandingAndEvent();
        }
        this.dataSync.updateTrip().then(result => {
          if (!result.success) {
            this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
            this.insights.trackException(result.error);
          }
        });
      } else if (isNew) {
        this.fishingEvent.catchItems.splice(catchIndex, 1);
      }
      this.updateLatLonValidators(this.setDetailForm.get('hoursFished')?.value);
      this.updateHoursFishedValidators();
    });
  }

  /**
   * Delete the current set
   */
  async deleteSet(): Promise<boolean> {
    // If no valid set exists, resolve immediately.
    if (this.setIndex === -1 && this.partOfEditedLanding === -1) {
      return Promise.resolve(true);
    }

    const res = await this.dataSync.deleteFishingEvent(this.fishingEvent.fishingEventID, this.partOfEditedLanding !== -1, 'HMS');
    if (res) {
      if (this.partOfEditedLanding !== -1) {
        this.navigateToDelivery();
      } else {
        this.router.navigate(['menu'], {queryParams: {fishery: 1 } });
      }
      return true;
    } else {
      alert('Deletion failed');
      return false;
    }
  }


  /**
   * Navigate to previous set in set list
   * @returns 
   */
  previousSet(): void {
    const sorted = this.getSortedSets();
    const currentPos = sorted.findIndex(
      s => s.fishingEventID === this.fishingEvent.fishingEventID
    );
    if (currentPos > 0) {
      const targetId   = sorted[currentPos - 1].fishingEventID;
      const targetIdx  = this.dataSync.user_trip().sets.findIndex(s => s.fishingEventID === targetId);
      this.router.navigate(['set_detail_hms', targetIdx, false]);
    }
  }


  /**
   * Create a new set at end of set list and go to it.
   */
  addSet() {
    if (!this.dataSync.storage_open) return;
    let fs = new FishingEvent('', "HMS");
    fs.fishingEventID = 'Set-' + (this.dataSync.new_set_num).toString();
    this.dataSync.new_set_num.update(num => num + 1)
    
    this.dataSync.storeFishingEvent(fs).then(res => {
      if (res.success) {
        this.partOfLandingInd = -1;
        this.partOfEditedLanding = -1;
        this.router.navigate(['set_detail_hms', this.dataSync.user_trip()?.sets.findIndex(s => s.fishingEventID == res.data), true]);
      } else {
        this.toastr.error('Error creating new set', 'Error');
        this.insights.trackException(res.error);
      }
      
      this.disableAdd = false;
    }).catch(_ => this.disableAdd = false);

    if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    this.setDetailForm.markAsUntouched();
  }


  /**
   * Navigate to next set in set list
   * @returns 
   */
  nextSet(): void {
    const sorted = this.getSortedSets();
    const currentPos = sorted.findIndex(
      s => s.fishingEventID === this.fishingEvent.fishingEventID
    );
    if (currentPos !== -1 && currentPos < sorted.length - 1) {
      const targetId   = sorted[currentPos + 1].fishingEventID;
      const targetIdx  = this.dataSync.user_trip()!.sets.findIndex(s => s.fishingEventID === targetId);
      this.router.navigate(['set_detail_hms', targetIdx, false]);
    }
  }
  
    /** Return a shallow, date-sorted copy of the sets array. 
     *  Matches the ordering that the main menu has
     **/
  private getSortedSets(): FishingEvent[] {
    return [...this.dataSync.user_trip()!.sets.filter(s => s.fishery == 'HMS')].sort((a, b) =>
      (a.startDate ? new Date(a.startDate).getTime() : Number.MIN_SAFE_INTEGER) -
      (b.startDate ? new Date(b.startDate).getTime() : Number.MIN_SAFE_INTEGER)
    );
  }


  navigateToDelivery() {
    if (this.setIndex == -1 && this.partOfEditedLanding != -1) {
      this.router.navigate(['hms_delivery_detail', 2, this.dataSync.received_logbooks().findIndex(rl => rl.sets.some(s => s.fishingEventID == this.fishingEvent.fishingEventID))], { queryParams: { previous: 'set_detail_hms', setIndex: this.setIndex } })
    } else if (this.setIndex != -1) {
      this.router.navigate(['hms_delivery_detail', 0, -1], { queryParams: { previous: 'set_detail_hms', setIndex: this.setIndex } });
    }
  }


  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
    if (this.locationSubscription) this.locationSubscription.unsubscribe();
    if (this.userSetsSubscription) this.userSetsSubscription.unsubscribe();
    //Edited landing subscription
  }


  /**
   * Highlight fields that are in an error state.
   */
  openErrorDialog() {
    //Looks like adding/removing validators is the solution here, need to finesse a bit though since this adds but doesn't remove
    this.setDetailForm.markAllAsTouched();
    console.log(this.setDetailForm)
    if (this.setDetailForm.valid && this.stringNum.valid)
      this.toastr.success('No errors on this set', 'Valid');
    else
      this.toastr.warning('Errors on this set, see fields', 'Invalid');
  }



/**
 * Checks if the set is new and untouched, and deletes it if so before navigating away.
 * @param targetRoute The route to navigate to (e.g., 'menu' or home).
 */
  handleExit(targetRoute: string) {
    console.log("targetRoute: ", targetRoute);
  if (this.isNewSet && this.setDetailForm.pristine && this.stringNum.pristine) {
    // Call deleteSet (navigation done within deleteSet)
    this.deleteSet().then((res) => {
      if (res) {
        console.log('New, untouched set deleted before navigation.');
      } else {
        // If deletion failed, you may wish to still navigate or alert the user.
        console.error('Deletion failed.');
      }
    });
  } else {
    // If we came from drafts, navigate back there
    if (targetRoute == 'draft') {
      this.router.navigate(['hms_delivery_detail', 3, this.partOfDraftTrip]);
    } else if (targetRoute == 'delivery') {
      this.router.navigate(['hms_delivery_detail', this.isReadOnly ? 1 : this.partOfEditedLanding != -1 ? 2 : 0, 
        this.isReadOnly ? this.dataSync.received_logbooks().findIndex(del => del.sets.some(s => s.fishingEventID == this.fishingEvent.fishingEventID)) : this.partOfEditedLanding != -1 ? this.partOfEditedLanding : this.setIndex]);
    } else { 
      // If the form has been touched (or it’s an existing set), just navigate.
      this.router.navigate([targetRoute], { queryParams: { fishery: 1 } });
    }

  }
}


  _forbidNonNumeric(control: AbstractControl) {
    control.setValue(control.value.replace(/[^0-9]/g, ''), { onlySelf: true, emitEvent: false });
  }

}

